import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  RiCloseLine,
  RiFileCopyLine,
  RiInformationLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from '@remixicon/react';
import styles from './ImageContentModal.style.module.css';
import IconButton from '../../IconButton';
import DownloadIcon from '../../../atoms/DownloadIcon';
import { translate } from '../../../../localization';
import PointingTooltip, {
  TooltipPosition,
} from '../../../atoms/PointingTooltip';

interface ImageContentModalProps {
  imageSrc: string;
  imageAlt: string;
  content?: string;
  onClose: () => void;
  current: number;
  total: number;
  handlePageChange: (newIndex: number) => void;
  onImageDownload: (src: string, title: string) => void;
}

const ImageContentModal: React.FC<ImageContentModalProps> = ({
  imageSrc,
  imageAlt,
  content,
  onClose,
  current,
  total,
  handlePageChange,
  onImageDownload,
}) => {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const [resolution, setResolution] = useState<string | null>(null);

  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setResolution(`${img.naturalWidth} x ${img.naturalHeight}`);
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && current > 1) {
        handlePageChange(current - 2);
      } else if (event.key === 'ArrowRight' && current < total) {
        handlePageChange(current);
      } else if (event.key === 'Escape') {
        onClose();
      }
    },
    [current, total, handlePageChange, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // prevent background scroll

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto'; // restore scroll
    };
  }, [handleKeyDown]);

  const modalContent = (
    <div
      className={styles.backdrop}
      role='dialog'
      aria-modal='true'
      aria-label='Image Viewer Modal'
    >
      <div className={styles.modal}>
        <div className={styles.topButtonContainer}>
          <IconButton
            onClick={() => onImageDownload(imageSrc, imageAlt)}
            className={styles.downloadBtn}
            ariaLabel='Download Image'
            type='button'
          >
            <DownloadIcon />
            {translate('download')}
          </IconButton>

          <button
            className={styles.closeButton}
            onClick={onClose}
            type='button'
            aria-label='Close Modal'
          >
            <RiCloseLine />
          </button>
        </div>

        <div className={styles.imageSectionContainer}>
          <div>
            <IconButton
              className={styles.navButton}
              onClick={() => handlePageChange(current - 2)}
              disabled={current - 1 === 0}
              ariaLabel='Previous Image'
              type='button'
            >
              <RiArrowLeftSLine />
            </IconButton>
          </div>

          <div className={styles.gridContainer}>
            <div className={styles.row}>
              <div className={styles.imageCounter}>
                Image {current}/{total}
              </div>
              <div className={styles.emptyContainer} />
            </div>

            <div className={styles.row}>
              <div
                className={`${styles.imageSection} ${
                  !content ? styles.centerImage : ''
                }`}
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className={styles.image}
                  onLoad={handleImageLoad}
                />
              </div>

              {content && (
                <div className={styles.contentSection}>
                  <div className={styles.contentHeadingContainer}>
                    <h3 className={styles.heading}>AI Generated Description</h3>
                    <RiFileCopyLine
                      size={24}
                      color='#0000C9'
                      className={styles.copyButtonIcon}
                    />
                  </div>
                  <p className={styles.content}>{content}</p>
                </div>
              )}
            </div>

            {resolution && (
              <div className={styles.row}>
                <div className={styles.imageDimensionsSection}>
                  <PointingTooltip
                    text={translate('image_carousel_tooltip_text')}
                    showTooltip={isTooltipVisible}
                    onMouseEnter={showTooltip}
                    toolTipPosition={TooltipPosition.BOTTOM_CENTER}
                    onMouseLeave={hideTooltip}
                    id='image-dimensions-tooltip'
                    classes={styles.imageDimensionsTooltip}
                  >
                    <RiInformationLine
                      size={12}
                      className={styles.tooltipInfoIcon}
                    />
                  </PointingTooltip>
                  <span className={styles.imageDimensions}>{resolution}</span>
                </div>
                <div className={styles.emptyContainer} />
              </div>
            )}
          </div>

          <div>
            <IconButton
              className={styles.navButton}
              onClick={() => handlePageChange(current)}
              disabled={current === total}
              ariaLabel='Next Image'
              type='button'
            >
              <RiArrowRightSLine />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal
  return ReactDOM.createPortal(
    modalContent,
    document.getElementById('modal-root')!
  );
};

export default ImageContentModal;
